import { useState, useEffect, useCallback } from 'react';
import { CookieConsent, defaultConsent, euDefaultConsent } from '@/types';
import { isEURegion } from '@/src/utils/region';

export const useCookieConsent = () => {
  const [consent, setConsentState] = useState<CookieConsent | null>(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('eNvesta AI_cookie_consent');
      if (stored) {
        try {
          setConsentState(JSON.parse(stored));
        } catch (e) {
          console.warn('Stale/corrupted cookie consent found, reset to default keys:', e);
          const defaultToUse = isEURegion() ? euDefaultConsent : defaultConsent;
          setConsentState(defaultToUse);
        }
      } else if (isEURegion()) {
        setShowBanner(true);
        setConsentState(euDefaultConsent);
      } else {
        setConsentState(defaultConsent);
      }
    }
  }, []);

  const setConsent = useCallback((newConsent: CookieConsent) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('eNvesta AI_cookie_consent', JSON.stringify(newConsent));
      setConsentState(newConsent);
      setShowBanner(false);

      // Apply consent to Firebase Analytics dynamically
      import('firebase/analytics')
        .then(({ getAnalytics, setAnalyticsCollectionEnabled }) => {
          try {
            const analytics = getAnalytics();
            setAnalyticsCollectionEnabled(analytics, Boolean(newConsent.analytics));
          } catch (e) {
            console.debug('Firebase Analytics is not initialized or supported in this browser:', e);
          }
        })
        .catch(() => {
          // Quietly ignore if firebase/analytics is not available
        });
    }
  }, []);

  const updateConsent = useCallback((updates: Partial<CookieConsent>) => {
    if (!consent) return;
    const updated = { ...consent, ...updates, timestamp: Date.now() };
    setConsent(updated);
  }, [consent, setConsent]);

  return { consent, showBanner, setConsent, updateConsent, setShowBanner };
};
